/**
 * CR168: Hook for managing merchant aliases
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { MerchantAlias } from "@/lib/flyer-bucket/types";

export function useMerchantAliases(merchantId?: string) {
  const { toast } = useToast();
  const [aliases, setAliases] = useState<MerchantAlias[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAliases = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("merchant_aliases")
        .select("*")
        .order("priority", { ascending: false })
        .order("alias");

      if (merchantId) {
        query = query.eq("merchant_id", merchantId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAliases((data || []) as MerchantAlias[]);
    } catch (err) {
      console.error("Failed to fetch aliases:", err);
      toast({
        title: "Error",
        description: "Failed to load merchant aliases",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [merchantId, toast]);

  const createAlias = async (
    merchant_id: string,
    alias: string,
    alias_type: string = "filename",
    priority: number = 0
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("merchant_aliases")
        .insert({
          merchant_id,
          alias,
          alias_type,
          priority,
        });

      if (error) throw error;

      toast({
        title: "Created",
        description: `Alias "${alias}" created successfully`,
      });

      await fetchAliases();
      return true;
    } catch (err: any) {
      console.error("Failed to create alias:", err);
      toast({
        title: "Error",
        description: err.message?.includes("duplicate") 
          ? "This alias already exists for this type"
          : err.message || "Failed to create alias",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateAlias = async (id: string, updates: Partial<MerchantAlias>): Promise<boolean> => {
    try {
      // Only send mutable fields — alias_normalized is GENERATED ALWAYS
      const { alias, alias_type, priority, merchant_id } = updates;
      const safeUpdates: Record<string, unknown> = {};
      if (alias !== undefined) safeUpdates.alias = alias;
      if (alias_type !== undefined) safeUpdates.alias_type = alias_type;
      if (priority !== undefined) safeUpdates.priority = priority;
      if (merchant_id !== undefined) safeUpdates.merchant_id = merchant_id;

      const { error } = await supabase
        .from("merchant_aliases")
        .update(safeUpdates)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Updated",
        description: "Alias updated successfully",
      });

      await fetchAliases();
      return true;
    } catch (err: any) {
      console.error("Failed to update alias:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update alias",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteAlias = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("merchant_aliases")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Alias deleted successfully",
      });

      await fetchAliases();
      return true;
    } catch (err: any) {
      console.error("Failed to delete alias:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete alias",
        variant: "destructive",
      });
      return false;
    }
  };

  // Note: is_enabled column would need to be added to merchant_aliases table if needed
  // For now, aliases are always enabled - delete to disable

  useEffect(() => {
    fetchAliases();
  }, [fetchAliases]);

  return {
    aliases,
    loading,
    fetchAliases,
    createAlias,
    updateAlias,
    deleteAlias,
  };
}
